import type { FC } from 'react'

import { useUnit } from 'effector-react'

import { CreatorsListDialog } from '@/components/ui/creators-list-dialog.tsx'
import type { DialogProps } from '@/dialogs/types'
import { $creators, $currentCreator } from '@/models/creators.ts'
import { copyScriptsFx } from '@/models/scripts/scripts.ts'
import type { Creator } from '@/modules/creators/types.ts'

import type { SelectedItemsMap } from '../../modules/scripts/components/ScriptView.tsx'

export interface Props extends DialogProps {
  scriptsMap: SelectedItemsMap
  onResetRelectedScripts: () => void
}

export const CopyScriptDialog: FC<Props> = ({
  isOpen,
  onClose,
  scriptsMap,
  onResetRelectedScripts,
}) => {
  const creators = useUnit($creators)
  const activeCreator = useUnit($currentCreator)
  const isLoading = useUnit(copyScriptsFx.pending)

  const availableCreators = creators.filter(
    (creator) =>
      activeCreator &&
      creator.id !== activeCreator.id &&
      creator.licenseId &&
      creator.permissions.includes('MODIFY_SETTINGS'),
  )

  if (!activeCreator) return null

  const handleSubmit = async (selectedCreator: Creator[]) => {
    const folders = [...scriptsMap.entries()].map(([folderId, scripts]) => ({
      id: folderId,
      scriptIds: [...scripts.keys()],
    }))

    await copyScriptsFx({
      originCreatorId: activeCreator?.id,
      targetCreatorIds: selectedCreator.map(({ id }) => id),
      folders: folders,
    })
    onResetRelectedScripts()
  }

  return (
    <CreatorsListDialog
      multiple
      open={isOpen}
      onClose={onClose}
      creators={availableCreators}
      title="Copy to other Creators"
      noCreatorMessage="There are no creators the script to be copied to"
      description="Select the creators that the script should be copied to"
      onSubmit={handleSubmit}
      submitButtonLabel="Confirm"
      isSubmitButtonLoading={isLoading}
    />
  )
}
